import { FC } from 'react';
import { Dialog, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { updatePassword } from 'entities/Auth/api';
import { useAuthStore } from 'processes/auth/model/state';
import { Paper } from 'shared/ui/Paper';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { PasswordForm } from 'shared/ui/PasswordForm';
import { ChangePasswordValues } from 'shared/ui/PasswordForm/consts/validation';

const PasswordExpiredModal: FC = () => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { isPasswordExpired, setPasswordExpired } = useAuthStore();
	const { mutate: changePassword, isLoading } = useMutation({ mutationFn: updatePassword });

	const handleSubmit = (values: ChangePasswordValues) => {
		changePassword(
			{ password: values.password, currentPassword: values.currentPassword },
			{
				onSuccess: async () => {
					await queryClient.refetchQueries({ queryKey: ['validate-token'] });
					setPasswordExpired(false);
					enqueueSnackbar({ message: t('shared_texts.success_update'), variant: 'success' });
				},
				onError: (err) => {
					showErrorNotification(err as AxiosError);
				},
			},
		);
	};

	return (
		<Dialog
			open={isPasswordExpired}
			disableEscapeKeyDown
			PaperComponent={Paper}
			PaperProps={{ title: t('password_expired.title') as string }}
		>
			<Typography variant={'body1'} sx={{ marginBottom: '24px' }}>
				{t('password_expired.description')}
			</Typography>
			<PasswordForm mode="expired" onSubmit={handleSubmit} isLoading={isLoading} />
		</Dialog>
	);
};

export default PasswordExpiredModal;
